import * as React from 'react';
import { TwiddleRowContext, TwiddleOpenContext } from './contexts';

/**
  Utility hook to access the state of a twiddle row. Connects to the context
  coming from the <NewTable> parent.

   The state of a twiddle is defined by three things, in order of precedence:
    1. The initial value of the <TwiddleRow> open prop
    2. The table's "expand all" state, except when overridden
    3. The state of the twiddle row itself
 */
export const useTwiddle = (id: string | number, openProp = false) => {
  const {
    getOpenState, //
    isRowRegistered,
    makeSetOpen,
    toggle,
    registerTwiddleRow,
  } = React.useContext(TwiddleOpenContext);

  React.useLayoutEffect(() => {
    registerTwiddleRow(id, openProp);
  }, [id, openProp, registerTwiddleRow]);

  return React.useMemo(
    () => ({
      id,
      // if the row is registered, we go by the state; if not, we use the prop
      open: isRowRegistered(id) ? getOpenState(id) : openProp,
      setOpen: makeSetOpen(id, openProp),
      toggle: () => toggle(id),
    }),
    [id, openProp, getOpenState, isRowRegistered, makeSetOpen, toggle],
  );
};

export const useTwiddleIconState = () => {
  const { id, open, level } = React.useContext(TwiddleRowContext);
  return React.useMemo(
    () => ({
      id,
      level,
      open,
    }),
    [id, level, open],
  );
};

export const useTwiddleExpandAllState = () => {
  const { toggleAll, areAllRowsOpen } = React.useContext(TwiddleOpenContext);

  return React.useMemo(
    () => ({
      open: areAllRowsOpen,
      toggleAll,
    }),
    [toggleAll, areAllRowsOpen],
  );
};
